import { useEffect } from 'react';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import { ArrowRightOutlined } from 'antd/icons';
import * as actions from '@actions';
import { useQuery } from '@tanstack/react-query';
import useAuth from '@vl-core/useAuth';
import { useQueryState, parseAsBoolean } from '@lib/queryState';
import useRouter from '@vl-core/useRouter';
import CreateCase from '../../Modal/CreateCase';
import { NEW_HBS_LOGO } from '@lib/context/themes';

const Wrapper = styled.div`
  width: 100%;
  margin-bottom: 1rem;
`;

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: fit-content;
  z-index: 1;
  position: relative;
`;

const WelcomeHeading = styled.h1`
  margin-bottom: 0.25rem;
  font-family: var(--fontPrimary);
  font-weight: 700;
  font-size: 1.375rem;
  line-height: 1.75rem;

  @media (min-width: 768px) {
    margin-bottom: 0.5rem;
    font-size: 2rem;
    line-height: 2.125rem;
  }
`;

const SubtitleWrapper = styled.div`
  font-size: 0.875rem;
  line-height: 1.125rem;
  z-index: 1;
  color: var(--productGray);
  margin-bottom: 1rem;

  @media (min-width: 768px) {
    margin-bottom: 2rem;
    width: 65%;
    font-size: 1rem;
    line-height: 1.4375rem;
  }
`;

const WelcomeText = styled.span`
  &:before {
    content: var(--welcome-text);
  }
`;

const SelfReferButton = styled(Button)`
  color: #fff;
  z-index: 99;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const LogoImage = styled.img`
  position: absolute;
  top: 0;
  right: 0;
  width: 250px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const WelcomeDashboard = ({ onSelfRefer }: { onSelfRefer: (selRefer: number | null) => void }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const [reqAssessment, setReqAssessment] = useQueryState('createCase', parseAsBoolean);

  const { query } = useRouter();

  useEffect(() => {
    if (query.createCase === 'true') {
      setReqAssessment(true);
    }
  }, [query]);

  const canSelfRefer = user?.self_refer && user?.insured;

  const { isLoading: isLoadingSurveyJsDefinition, data: { surveyjs_defs } = { surveyjs_defs: [] } } = useQuery({
    queryKey: ['useSurvey'],
    queryFn: () => dispatch(actions.getSurveyJsDefinition({ cat_code: 'UI', event_code: 'SR', current: 'true' }))
  });
  const noSurveyDefinition = surveyjs_defs?.length > 0;

  useEffect(() => {
    const newCaseModalRequested = !!new URLSearchParams(window.location.search).get('newcase');

    if (newCaseModalRequested) {
      setReqAssessment(true);
    }
  }, []);

  return (
    <Wrapper>
      <ContentLayout>
        <WelcomeHeading>
          {t('Hello ')}
          {user?.first_name}!
        </WelcomeHeading>
        <SubtitleWrapper>
          <WelcomeText />
        </SubtitleWrapper>
        {canSelfRefer && noSurveyDefinition && (
          <SelfReferButton
            type="primary"
            size="large"
            onClick={() => setReqAssessment(true)}
            data-testid="request-assessment"
          >
            {t('Get expert help for a new condition')} <ArrowRightOutlined aria-hidden />
          </SelfReferButton>
        )}

        <LogoImage src={NEW_HBS_LOGO} alt={t('HBS UK logo')} />
      </ContentLayout>

      <CreateCase
        open={!!reqAssessment}
        setOpen={setReqAssessment}
        surveyJsDefinition={surveyjs_defs?.[0]}
        isLoadingSurveyJsDefinition={isLoadingSurveyJsDefinition}
        onSelfRefer={onSelfRefer}
      />
    </Wrapper>
  );
};

export default WelcomeDashboard;
